import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FilaDoceItem,
  FilaSalgadaItem,
  FilaEstatisticas,
  PedidoFoodflyResumo,
  filaPizzasService,
  FilaSalgadaDescricao,
  FilaDoceDescricao,
} from "../../../services/filaPizzasService";
import { useAuth } from "../../../context/AuthContext";
import { useFilaIntegracaoEvents } from "../../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import { lojaService } from "../../../services/lojaService";

type StatusFilter = "all" | "pending" | "done";

interface FilterState {
  lojaId: string;
  displayId: string;
  orderId: string;
}

/** Item de fila salão (FoodFly) na listagem admin. */
export type FilaSalaoListItem = {
  id: number;
  pedidoFoodflyId: number;
  displayId: number;
  orderId: number;
  descricao: Array<FilaSalgadaDescricao | FilaDoceDescricao>;
  status: string;
  observation?: string | null;
  createdAt: string;
  updatedAt?: string;
  pedidoFoodfly?: PedidoFoodflyResumo | null;
  assignedToUser?: { id: number; nome: string } | null;
};

const getTodayISOInSaoPaulo = (): string => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
};

interface FilasIntegracaoContextType {
  filaSalgada: FilaSalgadaItem[];
  filaDoce: FilaDoceItem[];
  filaSalaoSalgada: FilaSalaoListItem[];
  filaSalaoDoce: FilaSalaoListItem[];
  currentPageSalgada: number;
  currentPageDoce: number;
  currentPageSalaoSalgada: number;
  currentPageSalaoDoce: number;
  totalPagesSalgada: number;
  totalPagesDoce: number;
  totalPagesSalaoSalgada: number;
  totalPagesSalaoDoce: number;
  totalItemsSalgada: number;
  totalItemsDoce: number;
  totalItemsSalaoSalgada: number;
  totalItemsSalaoDoce: number;
  itemsPerPage: number;
  isLoadingSalgada: boolean;
  isLoadingDoce: boolean;
  isLoadingSalaoSalgada: boolean;
  isLoadingSalaoDoce: boolean;
  isLoadingStats: boolean;
  salgadaStatus: StatusFilter;
  doceStatus: StatusFilter;
  salaoSalgadaStatus: StatusFilter;
  salaoDoceStatus: StatusFilter;
  filters: FilterState;
  lojas: Array<{ id: number; nome: string }>;
  filterErrors: Record<string, string>;
  selectedDateISO: string;
  setSelectedDateISO: (iso: string) => void;
  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => boolean;
  handleClear: () => void;
  clearFilterErrors: () => void;
  setSalgadaStatus: (status: StatusFilter) => void;
  setDoceStatus: (status: StatusFilter) => void;
  setSalaoSalgadaStatus: (status: StatusFilter) => void;
  setSalaoDoceStatus: (status: StatusFilter) => void;
  setCurrentPageSalgada: (page: number) => void;
  setCurrentPageDoce: (page: number) => void;
  setCurrentPageSalaoSalgada: (page: number) => void;
  setCurrentPageSalaoDoce: (page: number) => void;
  stats: FilaEstatisticas | null;
}

const FilasIntegracaoContext = createContext<
  FilasIntegracaoContextType | undefined
>(undefined);

const toApiStatus = (status: StatusFilter): string | undefined =>
  status === "all" ? undefined : status;

export const FilasIntegracaoProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [salgadaStatus, setSalgadaStatus] = useState<StatusFilter>("all");
  const [doceStatus, setDoceStatus] = useState<StatusFilter>("all");
  const [salaoSalgadaStatus, setSalaoSalgadaStatus] =
    useState<StatusFilter>("all");
  const [salaoDoceStatus, setSalaoDoceStatus] = useState<StatusFilter>("all");
  const [currentPageSalgada, setCurrentPageSalgada] = useState(1);
  const [currentPageDoce, setCurrentPageDoce] = useState(1);
  const [currentPageSalaoSalgada, setCurrentPageSalaoSalgada] = useState(1);
  const [currentPageSalaoDoce, setCurrentPageSalaoDoce] = useState(1);
  const [selectedDateISO, setSelectedDateISO] = useState<string>(
    getTodayISOInSaoPaulo()
  );
  const [filters, setFilters] = useState<FilterState>({
    lojaId: "",
    displayId: "",
    orderId: "",
  });
  const [searchFilters, setSearchFilters] = useState<FilterState>({
    lojaId: "",
    displayId: "",
    orderId: "",
  });
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});
  const itemsPerPage = 10;

  const { data: lojas = [] } = useQuery({
    queryKey: ["filas-integracao", "lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaIdParamRaw = searchFilters.lojaId?.trim() || "";
  const parsedLojaIdParam = Number(lojaIdParamRaw);
  const lojaIdParam =
    lojaIdParamRaw !== "" && Number.isFinite(parsedLojaIdParam)
      ? parsedLojaIdParam
      : undefined;
  const displayIdParam = searchFilters.displayId?.trim() || undefined;
  const orderIdParam = searchFilters.orderId?.trim() || undefined;

  useFilaIntegracaoEvents(user?.lojaId, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.filasIntegracao,
  });

  const listParams = {
    data: selectedDateISO,
    lojaId: lojaIdParam,
    displayId: displayIdParam,
    orderId: orderIdParam,
    limit: itemsPerPage,
  };

  const { data: salgadaResponse, isLoading: isLoadingSalgada } = useQuery({
    queryKey: [
      "filas-integracao",
      "salgada",
      salgadaStatus,
      selectedDateISO,
      currentPageSalgada,
      itemsPerPage,
      lojaIdParam,
      displayIdParam,
      orderIdParam,
    ],
    queryFn: () =>
      filaPizzasService.getFilaSalgada({
        ...listParams,
        status: toApiStatus(salgadaStatus),
        page: currentPageSalgada,
      }),
  });

  const { data: doceResponse, isLoading: isLoadingDoce } = useQuery({
    queryKey: [
      "filas-integracao",
      "doce",
      doceStatus,
      selectedDateISO,
      currentPageDoce,
      itemsPerPage,
      lojaIdParam,
      displayIdParam,
      orderIdParam,
    ],
    queryFn: () =>
      filaPizzasService.getFilaDoce({
        ...listParams,
        status: toApiStatus(doceStatus),
        page: currentPageDoce,
      }),
  });

  const { data: salaoSalgadaResponse, isLoading: isLoadingSalaoSalgada } =
    useQuery({
      queryKey: [
        "filas-integracao",
        "salao_salgada",
        salaoSalgadaStatus,
        selectedDateISO,
        currentPageSalaoSalgada,
        itemsPerPage,
        lojaIdParam,
        displayIdParam,
        orderIdParam,
      ],
      queryFn: () =>
        filaPizzasService.getFilaSalaoSalgada({
          ...listParams,
          status: toApiStatus(salaoSalgadaStatus),
          page: currentPageSalaoSalgada,
        }),
    });

  const { data: salaoDoceResponse, isLoading: isLoadingSalaoDoce } = useQuery({
    queryKey: [
      "filas-integracao",
      "salao_doce",
      salaoDoceStatus,
      selectedDateISO,
      currentPageSalaoDoce,
      itemsPerPage,
      lojaIdParam,
      displayIdParam,
      orderIdParam,
    ],
    queryFn: () =>
      filaPizzasService.getFilaSalaoDoce({
        ...listParams,
        status: toApiStatus(salaoDoceStatus),
        page: currentPageSalaoDoce,
      }),
  });

  const { data: stats = null, isLoading: isLoadingStats } = useQuery({
    queryKey: ["filas-integracao", "stats", selectedDateISO],
    queryFn: () => filaPizzasService.getEstatisticas({ data: selectedDateISO }),
  });

  const filaSalgada = useMemo(
    () => salgadaResponse?.data || [],
    [salgadaResponse]
  );
  const filaDoce = useMemo(() => doceResponse?.data || [], [doceResponse]);
  const filaSalaoSalgada = useMemo(
    () => (salaoSalgadaResponse?.data || []) as FilaSalaoListItem[],
    [salaoSalgadaResponse]
  );
  const filaSalaoDoce = useMemo(
    () => (salaoDoceResponse?.data || []) as FilaSalaoListItem[],
    [salaoDoceResponse]
  );

  useEffect(() => {
    setCurrentPageSalgada(1);
  }, [salgadaStatus]);

  useEffect(() => {
    setCurrentPageDoce(1);
  }, [doceStatus]);

  useEffect(() => {
    setCurrentPageSalaoSalgada(1);
  }, [salaoSalgadaStatus]);

  useEffect(() => {
    setCurrentPageSalaoDoce(1);
  }, [salaoDoceStatus]);

  useEffect(() => {
    setCurrentPageSalgada(1);
    setCurrentPageDoce(1);
    setCurrentPageSalaoSalgada(1);
    setCurrentPageSalaoDoce(1);
  }, [selectedDateISO]);

  const handleFieldChange = useCallback((name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setFilterErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleSearch = useCallback((): boolean => {
    setFilterErrors({});
    setSearchFilters(filters);
    setCurrentPageSalgada(1);
    setCurrentPageDoce(1);
    setCurrentPageSalaoSalgada(1);
    setCurrentPageSalaoDoce(1);
    return true;
  }, [filters]);

  const clearFilterErrors = useCallback(() => setFilterErrors({}), []);

  const handleClear = useCallback(() => {
    setFilters({ lojaId: "", displayId: "", orderId: "" });
    setSearchFilters({ lojaId: "", displayId: "", orderId: "" });
    setCurrentPageSalgada(1);
    setCurrentPageDoce(1);
    setCurrentPageSalaoSalgada(1);
    setCurrentPageSalaoDoce(1);
  }, []);

  const value = useMemo(
    () => ({
      filaSalgada,
      filaDoce,
      filaSalaoSalgada,
      filaSalaoDoce,
      currentPageSalgada,
      currentPageDoce,
      currentPageSalaoSalgada,
      currentPageSalaoDoce,
      totalPagesSalgada: salgadaResponse?.totalPages || 0,
      totalPagesDoce: doceResponse?.totalPages || 0,
      totalPagesSalaoSalgada: salaoSalgadaResponse?.totalPages || 0,
      totalPagesSalaoDoce: salaoDoceResponse?.totalPages || 0,
      totalItemsSalgada: salgadaResponse?.total || 0,
      totalItemsDoce: doceResponse?.total || 0,
      totalItemsSalaoSalgada: salaoSalgadaResponse?.total || 0,
      totalItemsSalaoDoce: salaoDoceResponse?.total || 0,
      itemsPerPage,
      isLoadingSalgada,
      isLoadingDoce,
      isLoadingSalaoSalgada,
      isLoadingSalaoDoce,
      isLoadingStats,
      salgadaStatus,
      doceStatus,
      salaoSalgadaStatus,
      salaoDoceStatus,
      filters,
      filterErrors,
      selectedDateISO,
      setSelectedDateISO,
      handleFieldChange,
      handleSearch,
      handleClear,
      clearFilterErrors,
      setSalgadaStatus,
      setDoceStatus,
      setSalaoSalgadaStatus,
      setSalaoDoceStatus,
      setCurrentPageSalgada,
      setCurrentPageDoce,
      setCurrentPageSalaoSalgada,
      setCurrentPageSalaoDoce,
      stats,
      lojas,
    }),
    [
      filaSalgada,
      filaDoce,
      filaSalaoSalgada,
      filaSalaoDoce,
      currentPageSalgada,
      currentPageDoce,
      currentPageSalaoSalgada,
      currentPageSalaoDoce,
      salgadaResponse,
      doceResponse,
      salaoSalgadaResponse,
      salaoDoceResponse,
      itemsPerPage,
      isLoadingSalgada,
      isLoadingDoce,
      isLoadingSalaoSalgada,
      isLoadingSalaoDoce,
      isLoadingStats,
      salgadaStatus,
      doceStatus,
      salaoSalgadaStatus,
      salaoDoceStatus,
      filters,
      filterErrors,
      selectedDateISO,
      handleFieldChange,
      handleSearch,
      handleClear,
      clearFilterErrors,
      stats,
      lojas,
    ]
  );

  return (
    <FilasIntegracaoContext.Provider value={value}>
      {children}
    </FilasIntegracaoContext.Provider>
  );
};

export const useFilasIntegracao = () => {
  const context = useContext(FilasIntegracaoContext);
  if (!context) {
    throw new Error(
      "useFilasIntegracao deve ser usado dentro de FilasIntegracaoProvider"
    );
  }
  return context;
};
